import { find, has, is, defaultTo } from 'ramda';
import { ReactNode, useMemo } from 'react';

import { UIChatMessage } from '../chat.types.ts';

import { ChatUserMessageProps } from './ChatUserMessage.tsx';
import { MessageHandler, MessageVariant } from './ChatUserMessage.types.ts';
import { suggestionMessageHandler } from './variants/ChatSuggestionMessage/ChatSuggestionMessage.handler.ts';
import { ChatSuggestionMessage } from './variants/ChatSuggestionMessage/ChatSuggestionMessage.tsx';
import { defaultMessageHandler } from './variants/ChatUserDefaultMessage/ChatUserDefaultMessage.handler.ts';
import { ChatUserDefaultMessage } from './variants/ChatUserDefaultMessage/ChatUserDefaultMessage.tsx';
import { fileAttachmentMessageHandler } from './variants/ChatUserFileAttachmentMessage/ChatUserFileAttachmentMessage.handler.ts';
import { ChatUserFileAttachmentMessage } from './variants/ChatUserFileAttachmentMessage/ChatUserFileAttachmentMessage.tsx';
import { quickActionMessageHandler } from './variants/ChatUserQuickActionMessage/ChatUserQuickActionMessage.handler.ts';
import { ChatUserQuickActionMessage } from './variants/ChatUserQuickActionMessage/ChatUserQuickActionMessage.tsx';

const messageRegistry: Record<MessageVariant, (props: ChatUserMessageProps<any>) => ReactNode> = {
  [MessageVariant.FILE_ATTACHMENT]: ChatUserFileAttachmentMessage,
  [MessageVariant.DEFAULT]: ChatUserDefaultMessage,
  [MessageVariant.QUICK_ACTION]: ChatUserQuickActionMessage,
  [MessageVariant.SUGGESTION]: ChatSuggestionMessage,
};

/**
 * Returns the first annotation containing a valid type variant
 * First checks if the message has annotation in text parts (for stored messages),
 * then falls back to checking direct annotations, or returns default variant
 *
 * Using custom typing for `annotations` here temporarily.
 * #TODO: Refactor message variant handling with parts being source of truth and adding custom details through parts specifically
 */
const getMessageVariantAnnotation = (message: UIChatMessage) => {
  // First check if the message has annotation embedded in text parts (for messages loaded from storage)
  const textAnnotation = isMessageAnnotatedInText(message);

  if (textAnnotation) {
    return textAnnotation;
  }

  // Fall back to checking direct annotations (for new messages)
  return defaultTo({ type: MessageVariant.DEFAULT }, find(isMessageVariantAnnotation, message.annotations ?? []));
};

/**
 * Type assertion to ensure the annotation is a valid MessageVariant
 */
const isMessageVariantAnnotation = (annotation: any): annotation is { type: MessageVariant } =>
  is(Object, annotation) && // ignore primitives, null or undefined
  has('type', annotation) && // verify 'type' property exists
  Object.values(MessageVariant).includes(annotation.type as MessageVariant); // check whether it's a valid MessageVariant;

/**
 * Check if a message has annotation data stored in its text parts (for messages loaded from storage)
 * This handles cases where the message variant is embedded in the first text part as JSON.
 * see buildMessageParts in src/components/Chat/ChatUserMessage/variants/ChatUserFileAttachmentMessage/ChatUserFileAttachmentMessage.handler.ts
 */
const isMessageAnnotatedInText = (message: UIChatMessage): { type: MessageVariant } | null => {
  // Check if message has parts and at least one text part
  if (!message.parts || message.parts.length === 0) return null;

  const firstPart = message.parts[0];
  if (firstPart.type !== 'text') return null;

  if (message?.metadata?.type) {
    return { type: message?.metadata?.type };
  }

  try {
    // Try to parse the first part as JSON
    const parsed = JSON.parse(firstPart.text);

    // Check if it has annotations array with valid MessageVariant
    if (parsed.annotations && Array.isArray(parsed.annotations)) {
      const validAnnotation = parsed.annotations.find((annotation: any) => isMessageVariantAnnotation(annotation));
      if (validAnnotation) {
        return { type: validAnnotation.type };
      }
    }
  } catch {
    // Not valid JSON, return null
    return null;
  }

  return null;
};

export const useChatUserMessage = (message: UIChatMessage) => {
  const variantAnnotation = useMemo(() => getMessageVariantAnnotation(message), [message]);

  // Return the variant type instead of the rendered component
  return {
    variant: variantAnnotation.type,
    message,
  };
};

const submitRegistry: Record<MessageVariant, MessageHandler<any>['submit']> = {
  [MessageVariant.FILE_ATTACHMENT]: fileAttachmentMessageHandler.submit,
  [MessageVariant.QUICK_ACTION]: quickActionMessageHandler.submit,
  [MessageVariant.DEFAULT]: defaultMessageHandler.submit,
  [MessageVariant.SUGGESTION]: suggestionMessageHandler.submit,
};

export const getChatUserMessageSubmit = (messageVariant?: MessageVariant) => {
  return submitRegistry[messageVariant || MessageVariant.DEFAULT];
};

// Export the registry for use in the component
export { messageRegistry };
